import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Reveal } from "@/components/Section";

// Opening hook, always visible.
const INTRO = [
  "What if I could just apply what I need, the instant I need it?",
  "No bottle. No searching. No stopping what I am doing. Just there.",
  "During COVID, I was sanitizing my hands constantly. I would put everything down, reach for the bottle, sanitize, pick everything back up, and continue with my day.",
  "Then one evening I came out of a store and realized my sanitizer was locked inside my car. I remember standing there thinking about the absurdity of it.",
  "To get to the sanitizer, I first had to touch the car door. Open the car. Reach inside. Touch whatever else was in the way. Find the bottle. And only then could I sanitize my hands.",
  "The very thing that was supposed to protect me required me to touch more things before I could use it.",
  "I remember thinking: there has to be a better way. What if the protection I needed was already on me? Not something I had to remember. Not something buried in a bag. Not something sitting in my car. Something I could wear.",
  "At the time, it seemed like a relatively simple idea. It was not.",
];

// The rest, revealed on expand.
const REST = [
  {
    heading: "\u201CYou Are Not Going to Be Able to Do This.\u201D",
    paras: [
      "I started talking with injection molding specialists. Then more of them. Eventually, I had approached more than fifteen designers, engineers, manufacturers, and injection molding specialists around the world.",
      "Many of those conversations started positively. Interesting idea. Let us take a look. Maybe we can figure it out.",
      "Then the deeper we got into the engineering, the more complicated everything became. How do you dispense a liquid, lotion, or gel from something small enough to comfortably wear on your wrist? Do you use a straw? A spring? A pump? How does the reservoir breathe? How does air get back inside after material is dispensed? How do you generate enough pressure? How do you refill it? How do you create a valve small enough to fit within the device?",
      "And perhaps most importantly, how do you make all of those elements work together without the product leaking while someone is walking, running, surfing, exercising, or simply going about their day?",
      "One by one, people started telling me some version of the same thing: we cannot figure this out. Some of these were exactly the people and companies you would expect to call if you wanted to develop something like this. They could not solve it.",
      "I hired another team. They offered ideas. We made progress. We got close. But we still could not make every element work together.",
      "Eventually, I began hearing something even more discouraging: just give up. Somebody even said that if something like this could be done, someone would have already done it.",
      "Strangely, that did not make me want to stop. It made me more determined to understand why it was so difficult.",
    ],
  },
  {
    heading: "The Breakthroughs Came in Inches",
    paras: [
      "There was never one enormous flash of inspiration. The breakthroughs came little by little.",
      "First, I started figuring out how the cap could work. That solved one problem. Then came one of the hardest challenges of all. The valve.",
      "How do you create a valve in such a tiny space? How does the reservoir breathe? How does the material come out while preventing unwanted leakage? How can someone repeatedly press it, wear it, refill it, move with it, and trust that it will work when they need it?",
      "I started creating different configurations. Different internal geometries. Different openings. Different valve concepts. Different ways of generating pressure. Different ways of introducing air into the reservoir. Different ways of filling it. Different ways of sealing it. The possibilities seemed to multiply.",
      "And I found myself mentally imagining all of these components operating together. Pressure. Airflow. Material movement. The reservoir. The valve. The wrist. What happened when someone pressed the face. What happened immediately afterward.",
      "Then I would change something and try again. And again. And again.",
    ],
  },
  {
    heading: "Then Version Forty Arrived",
    paras: [
      "There were roughly forty different 3D printed versions. Each one taught me something. Most did not do exactly what I needed them to do.",
      "Then one arrived. I filled it. I pressed it. And it worked. Perfectly. The material shot nearly twenty feet into the air. And the device did not leak.",
      "That was the moment everything changed. After all of the conversations, all of the engineering questions, all of the configurations, all of the people who had told me they could not figure it out, I was suddenly holding a working answer in my hand.",
      "I remember thinking: we have it. It was a bizarre and exhilarating moment, because something that had existed primarily in my imagination and inside a digital 3D file was now physically doing exactly what I had imagined it could do.",
      "The fundamental question had finally been answered. Could a small wearable reservoir dispense effectively, remain sealed, and function on the wrist? Yes. It could.",
      "Of course, that was not the end. Turning a successful 3D printed prototype into a repeatable injection molded product created an entirely new set of challenges. Tooling. Materials. Tolerances. Wall thickness. Fit. Pressure. Valve placement. Manufacturing consistency. Fractions of a millimeter suddenly mattered.",
      "Something that behaved one way in a 3D printed prototype could behave completely differently when it came out of an injection mold. So began another cycle. Adjustment. Testing. Tool modification. Another test. Another adjustment. Another small breakthrough.",
      "It was an incredibly exciting process, because every time we solved another problem, the thing became more real. And those little glimpses of possibility kept pulling me forward.",
    ],
  },
  {
    heading: "Then I Realized It Was Bigger Than Sanitizer",
    paras: [
      "The irony is that the entire journey still went back to that incredibly simple moment outside the store. I wanted immediate access to sanitizer. But once the device began working, I started thinking differently.",
      "I am a surfer. Sometimes I am half a mile from shore. If I need sunscreen, I am not going to paddle half a mile back to the beach, find a bottle, apply it, and paddle back out again.",
      "Then I started thinking about other activities. Running. Cycling. Skiing. Hiking. Boating. Tennis. Outdoor work. Action sports have a very simple problem. No pockets. And even when you have a pocket, who wants to run around with a bottle of sunscreen inside it?",
      "Suddenly, WearSpray was not only about sanitizer. A friend of mine who was working at Amazon at the time looked at the device and said: what about mosquito repellent? Of course. That opened another door.",
      "Sunscreen. Mosquito repellent. Sanitizer. Skin treatments. Moisturizer. Aloe. Serums. Fragrance. Body mist. Potentially almost anything someone wants immediate access to that can be delivered as a liquid, lotion, or gel.",
      "What began as a solution to one frustrating moment was beginning to look like an entirely new way of carrying and accessing personal care and protection.",
    ],
  },
  {
    heading: "And Then Came the Watch",
    paras: [
      "As I showed WearSpray to people, I kept hearing another question. What if you put a watch on top of it? At first, it simply sounded like another interesting idea. Then I started thinking about what it could actually mean.",
      "Wearables have become incredibly good at sensing. They can measure. Track. Monitor. Analyze. Remind. But what happens after the reminder?",
      "A smartwatch might know you have been outside in intense UV exposure. It might tell you that it is time to reapply sunscreen. But what if the sunscreen were already on your wrist? What if the wearable could move from simply providing awareness to helping enable the physical action itself?",
      "That changed the way I thought about WearSpray again. Sense. Understand. Prompt. Act. The intelligence and the application could potentially exist in the same place.",
      "Then another question appeared. Why only the wrist? What about the back of a cell phone? The object almost everyone already carries every day. Again, the same underlying idea. Put what someone needs closer to the moment they actually need it. Why not?",
    ],
  },
  {
    heading: "There Has to Be a Better Way",
    paras: [
      "Looking back, I think that is what I was trying to solve from the very beginning. Not sanitizer. Not sunscreen. Not mosquito repellent. Access.",
      "There is a huge difference between knowing that you should do something and being able to do it at the moment it matters. We carry products around in bottles, tubes, containers, backpacks, purses, gym bags, cars, and beach bags. We forget them. We leave them behind. We do not want to carry them. And sometimes, when we finally need them, they simply are not there.",
      "WearSpray started with a very simple belief: some of the products we carry should become products we wear.",
      "The road to proving that was far more difficult than I ever imagined. There were many moments when people far more experienced than I was told me it could not be done. There were moments when I did not know exactly how it was going to be done either. But every small breakthrough created enough possibility to keep going.",
      "And eventually, after roughly forty 3D printed versions, the thing people told me could not be made was sitting in my hand. I pressed it. It sent a stream nearly twenty feet into the air. It did not leak. And then I put it on my wrist.",
      "That is the real story of WearSpray. It did not begin with a market study. It did not begin in a boardroom. It began outside a store, looking through the window of a locked car at a bottle of sanitizer I could not reach. And thinking: there has to be a better way.",
    ],
  },
];

function StoryBlock({ heading, paras }) {
  return (
    <div className="mt-12 first:mt-0">
      {heading && (
        <h3 className="font-heading text-2xl md:text-3xl font-light tracking-tight text-white leading-snug mb-6">
          {heading}
        </h3>
      )}
      <div className="space-y-5">
        {paras.map((p, i) => (
          <p key={i} className="font-body text-zinc-400 text-lg leading-relaxed">{p}</p>
        ))}
      </div>
    </div>
  );
}

export default function FounderStory() {
  const [open, setOpen] = useState(false);
  return (
    <section className="border-t border-zinc-800 bg-[#0d0d0d]" data-testid="about-founder-story">
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <span className="font-mono text-xs tracking-[0.25em] uppercase text-accent block mb-6">
            In the Founder&rsquo;s Words
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tight leading-[1.08]">
            It Started With a Thought I Could Not Shake
          </h2>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mt-10 space-y-5">
            {INTRO.map((p, i) => (
              <p key={i} className="font-body text-zinc-300 text-lg leading-relaxed">{p}</p>
            ))}
          </div>
        </Reveal>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="rest"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden"
              data-testid="about-founder-story-rest"
            >
              {REST.map((b) => (
                <StoryBlock key={b.heading} heading={b.heading} paras={b.paras} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          data-testid="about-founder-story-toggle"
          className="group inline-flex items-center gap-3 font-mono text-xs tracking-[0.22em] uppercase text-accent border border-accent/40 px-6 py-4 mt-12 hover:bg-accent hover:text-black transition-colors"
        >
          {open ? "Show less" : "Read the full story"}
          <ChevronDown
            size={16}
            className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>
    </section>
  );
}
